import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/apis';
import MexaLoader from '../../components/MexaLoader/MexaLoader';

// Lucide-style SVG Icons
const KYCIcons = {
  UserCheck: () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  Edit: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  CheckCircle: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  Clock: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  Save: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  X: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  Shield: () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  Plus: () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  )
};

const KYC = () => {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [kyc, setKyc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [form, setForm] = useState({
    email: '',
    pancard: '',
    aadharcard: '',
    tin_number: '',
    gst_number: '',
  });

  const { userData } = useAuth();
  const userId = userData?.id;

  const fetchKYC = async () => {
    try {
      const response = await api.get(`/customer/kyc/${userId}`);

      if (response.data) {
        setKyc(response.data);
        setForm({
          email: response.data.email || '',
          pancard: response.data.pancard || '',
          aadharcard: response.data.aadharcard || '',
          tin_number: response.data.tin_number || '',
          gst_number: response.data.gst_number || '',
        });
      } else {
        setKyc(null);
      }
      setMessage({ type: '', text: '' });
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setKyc(null);
        setMessage({ type: '', text: '' });
      } else {
        console.error('Failed to fetch KYC:', error);
        setKyc(null);
        setMessage({ type: 'error', text: 'Failed to load KYC data' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKYC();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let val = value;
    if (name === "aadharcard") {
      val = val.replace(/\D/g, "").slice(0, 12);
    } else if (name === "tin_number") {
      val = val.replace(/\D/g, "").slice(0, 11);
    } else if (name === "pancard") {
      val = val.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 10);
    } else if (name === "gst_number") {
      val = val.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 15);
    }
    setForm(prev => ({
      ...prev,
      [name]: val
    }));
  };

  const validateForm = () => {
    if (!form.email?.trim()) return "Email is required";
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(form.email)) {
      return "Please enter a valid email address";
    }

    if (form.pancard?.trim()) {
      if (!/^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/.test(form.pancard)) {
        return "Enter a valid 10-digit PAN card number (e.g. ABCDE1234F)";
      }
    }

    if (form.aadharcard?.trim()) {
      if (!/^\d{12}$/.test(form.aadharcard)) {
        return "Enter a valid 12-digit Aadhar card number";
      }
    }

    if (form.tin_number?.trim()) {
      if (!/^\d{11}$/.test(form.tin_number)) {
        return "Enter a valid 11-digit TIN number";
      }
    }

    if (form.gst_number?.trim()) {
      if (!/^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}Z[0-9A-Za-z]{1}$/.test(form.gst_number)) {
        return "Enter a valid 15-digit GSTIN (e.g. 22AAAAA0000A1Z5)";
      }
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setMessage({ type: 'error', text: validationError });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      await api.post(`/customer/kyc`, {
        user_id: userId,
        email: form.email?.trim(),
        pancard: form.pancard?.trim().toUpperCase(),
        aadharcard: form.aadharcard?.trim(),
        tin_number: form.tin_number?.trim(),
        gst_number: form.gst_number?.trim().toUpperCase(),
      });

      setMessage({
        type: 'success',
        text: editing ? 'KYC updated successfully!' : 'KYC submitted successfully!'
      });
      setEditing(false);
      await fetchKYC();
    } catch (error) {
      console.error('Failed to save KYC:', error);
      setMessage({
        type: 'error',
        text: 'Failed to save KYC. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (kyc) {
      setForm({
        email: kyc.email || '',
        pancard: kyc.pancard || '',
        aadharcard: kyc.aadharcard || '',
        tin_number: kyc.tin_number || '',
        gst_number: kyc.gst_number || '',
      });
    }
    setEditing(false);
    setMessage({ type: '', text: '' });
  };

  if (loading) {
    return (
      <div className="view-section">
        <div className="flex justify-center items-center py-12">
          <MexaLoader size="lg" text="Loading KYC info..." />
        </div>
      </div>
    );
  }

  return (
    <div className="view-section p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <h2 className="md:text-2xl text-xl font-bold text-gray-900">
          {editing ? 'Edit KYC Details' : 'KYC Information'}
        </h2>

        {!editing && kyc && (
          <button
            onClick={() => setEditing(true)}
            className="text-primary font-bold text-sm px-3 py-2 rounded-md border border-primary/20 hover:bg-primary/5 transition-colors flex items-center space-x-2"
          >
            <KYCIcons.Edit />
            <span>Edit Details</span>
          </button>
        )}
      </div>

      {/* Message Alert */}
      {message.text && (
        <div className={`mb-4 p-3 rounded ${message.type === 'success'
            ? 'bg-green-100 border border-green-400 text-green-700'
            : 'bg-red-100 border border-red-400 text-red-700'
          }`}>
          {message.text}
        </div>
      )}

      {!kyc && !editing && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-500">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No KYC details</h3>
          <p className="text-gray-600 text-sm mb-6">Complete your KYC verification to get started</p>
          <button
            onClick={() => setEditing(true)}
            className="bg-primary text-white px-6 py-2.5 rounded-lg font-medium hover:bg-primary/80 transition-colors text-sm"
          >
            Complete Your KYC
          </button>
        </div>
      )}

      {/* KYC Display - Read Only */}
      {kyc && !editing && (
        <div className="space-y-6">
          {/* Verification Status Card */}
          <div className="bg-white p-5 rounded-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${kyc.is_verified_admin ? 'bg-green-100' : 'bg-yellow-100'
                  }`}>
                  {kyc.is_verified_admin ? (
                    <KYCIcons.CheckCircle className="text-green-600" />
                  ) : (
                    <KYCIcons.Clock className="text-yellow-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-dark">
                    {kyc.is_verified_admin ? 'Verified' : 'Pending Verification'}
                  </h3>
                </div>
              </div>

              {kyc.verify_date && (
                <div className="text-right">
                  <p className="text-sm text-gray-500">Verified On</p>
                  <p className="font-medium text-dark">
                    {new Date(kyc.verify_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* KYC Details Card */}
          <div className="bg-white p-2">
            <h3 className="text-lg font-bold text-dark mb-4 -mt-4">Personal Details</h3>

            <div className="space-y-4">
              {[
                { label: 'Email Address', value: kyc.email },
                { label: 'PAN Card Number', value: kyc.pancard || 'Not provided' },
                { label: 'Aadhar Card Number', value: kyc.aadharcard || 'Not provided' },
                { label: 'TIN Number', value: kyc.tin_number || 'Not provided' },
                { label: 'GST Number', value: kyc.gst_number || 'Not provided' },
              ].map(({ label, value }, index) => (
                <div key={index} className="flex justify-between items-center py-3 border-b border-gray-100 last:border-b-0">
                  <div>
                    <p className="text-sm font-medium text-gray-500">{label}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-dark">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Security Note */}
          <div className="bg-blue-50  border border-blue-200 p-4 flex items-start space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <KYCIcons.Shield className="text-blue-600" />
            </div>
            <div>
              <p className="text-blue-800 font-medium text-sm">Your information is secure</p>
              <p className="text-blue-600 text-xs mt-1">
                All your KYC details are encrypted and stored securely. We comply with data protection regulations.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Edit KYC Form */}
      {editing && (
        <div className="bg-white md:p-2">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { field: 'email', label: 'Email Address', type: 'email', required: true },
                { field: 'pancard', label: 'PAN Card Number', type: 'text', required: false },
                { field: 'aadharcard', label: 'Aadhar Card Number', type: 'text', required: false },
                { field: 'tin_number', label: 'TIN Number', type: 'text', required: false },
                { field: 'gst_number', label: 'GST Number', type: 'text', required: false },
              ].map(({ field, label, type, required }) => (
                <div key={field} className={field === 'email' ? 'md:col-span-2' : ''}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label}
                    {required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    type={type}
                    name={field}
                    value={form[field]}
                    onChange={handleInputChange}
                    required={required}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                    placeholder={`Enter ${label.toLowerCase()}`}
                  />
                </div>
              ))}
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={submitting}
                className="bg-primary text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>{kyc ? 'Updating...' : 'Submitting...'}</span>
                  </>
                ) : (
                  <>
                    <KYCIcons.Save />
                    <span>{kyc ? 'Update KYC' : 'Save KYC'}</span>
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={submitting}
                className="px-6 py-2 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                <KYCIcons.X />
                <span>Cancel</span>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default KYC;